import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { PageLayout } from 'app/views/components/layout/page-layout'
import { Group, Modal } from '@mantine/core'
import PageTitle from 'app/views/components/Headers&Text/PageTitle'
import PrimaryButton from 'app/views/components/buttons/PrimaryButton'
import MainTable from 'app/views/components/Table/MainTable'
import {
  useHasPermission,
  CREATE_USERS_PERMISSION,
} from 'app/hooks/useHasPermission'
import { loadUsers } from 'app/state/ducks/users/thunks'
import { updateUserInState } from 'app/state/ducks/users/actions'
import { getUsers } from 'app/state/ducks/users/selectors'
import { userManagementCols } from './user-management-table.columnDefs'
import UserFormLogic, { SavedUser } from './user-form-logic'

export default function UserManagementPage() {
  const dispatch = useDispatch()
  const usersAction = useSelector(getUsers)
  const rawUsers: SavedUser[] = usersAction?.payload?.data ?? []
  const [recentlyCreated, setRecentlyCreated] = useState<SavedUser[]>([])
  const reduxIds = new Set(rawUsers.map((u) => u.id))
  const users = [
    ...rawUsers,
    ...recentlyCreated.filter((u) => !reduxIds.has(u.id)),
  ].map((u) => ({ ...u, fullName: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() }))

  const [openUserModal, setOpenUserModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<SavedUser | undefined>(undefined)
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create')
  const canManageUsers = useHasPermission(CREATE_USERS_PERMISSION)

  useEffect(() => {
    dispatch(loadUsers())
  }, [dispatch])

  function openCreateModal() {
    if (!canManageUsers) return
    setModalMode('create')
    setOpenUserModal(true)
  }

  function openEditModal() {
    if (!canManageUsers || !selectedUser) return
    setModalMode('edit')
    setOpenUserModal(true)
  }

  function closeModal() {
    setOpenUserModal(false)
  }

  function onSaved(saved: SavedUser) {
    setSelectedUser(undefined)
    setRecentlyCreated((prev) => {
      const existsInRecent = prev.some((u) => u.id === saved.id)
      if (existsInRecent) {
        // Replace stale buffered entry with the latest payload.
        return prev.map((u) => (u.id === saved.id ? saved : u))
      }
      if (!rawUsers.some((u) => u.id === saved.id)) {
        // New create — buffer it until the list catches up.
        return [...prev, saved]
      }
      // Pure update of a user already in Redux; nothing to buffer.
      return prev
    })
    dispatch(updateUserInState(saved))
  }

  return (
    <PageLayout>
      <Group position="apart" mb="5%">
        <PageTitle>User Management</PageTitle>
        {canManageUsers && (
          <Group>
            <PrimaryButton onClick={openCreateModal}>Create User</PrimaryButton>
            <PrimaryButton onClick={openEditModal} disabled={!selectedUser}>Update User</PrimaryButton>
          </Group>
        )}
      </Group>

      <MainTable
        tableName='User Management Table'
        data={users}
        columnDefs={userManagementCols}
        setRow={(row: SavedUser) => setSelectedUser(row)}
        selectedRowId={selectedUser?.id ?? null}
        headerBackgroundColor='#F0EEEE'
        enableTopToolbar={false}
        withPagination={true}
      />

      <Modal
        size="50%"
        opened={openUserModal}
        onClose={closeModal}
        withCloseButton={false}
        styles={{
          header: {
            padding: 0,
            minHeight: 0,
          },
          body: {
            padding: '16px 40px 32px 40px',
          },
        }}
      >
        <UserFormLogic
          close={closeModal}
          mode={modalMode}
          user={modalMode === 'edit' && selectedUser ? {
            id: selectedUser.id,
            firstName: selectedUser.firstName,
            lastName: selectedUser.lastName,
            email: selectedUser.email,
            username: selectedUser.username,
            phoneNumber: selectedUser.phoneNumber ?? '',
            role: selectedUser.role,
            accessLevel: selectedUser.accessLevel,
            status: selectedUser.status,
            company: selectedUser.company,
            region: selectedUser.region,
            createdAt: selectedUser.createdAt || '',
          } : undefined}
          onSaved={onSaved}
        />
      </Modal>
    </PageLayout>
  )
}
